/* Helpful types when typing React code.

The policy is to type any code you touch.


HOW TO TYPE A FILE:

- Change file extension to `.tsx`.

- You might need to install types for libraries we import (e.g. `yarn add --dev @types/lodash`).

- You might need to convert any Javascript files we import into Typescript first too.
   TIP: If you don’t have time, then add `// @ts-ignore Javascript import` before the import statement.


HOW TO TYPE A COMPONENT:

- DON'T use types like React.ReactElement, React.ReactNode, or JSX.Element.
- DO use the type aliases from this document.

- DON'T use `interface` to declare types.
- DO Use `type` instead (https://stackoverflow.com/a/59561874).

- PREFER declaring prop types inline.

- DO use Children as the type for the children prop.
- DO use Component as the type for any other props that need a component.
- DO use Style as the type for style props.
- DO use ComponentReturn as the return type for components.

- For optional props, you can specify a default value, and then
   add a `?` to corresponding key in the type object.
- To return a string from a component, just wrap it in a fragment
   (e.g., <>My text here.</>). This way, the return type will still be a component.


EXAMPLE OF TYPED COMPONENT:

    const MyComponent = ({
        label,
        enabled = true,
        fallbackWhenDisabled = undefined,
        children = null,
    }: {
        label: string,
        enabled?: boolean,
        fallbackWhenDisabled?: Component | undefined
        children?: Children,
    }): ReturnComponent => {
        if (!enabled) {
            if (fallbackWhenDisabled === undefined) {
                return <>Nothing to show.</>;
            } else {
                return fallbackWhenDisabled;
            }
        }
        return <>
            <Typography>{label}</h1>
            {children}
        </>;
    }


OTHER TIPS:

- DON'T use default exports and imports.
- DO use explicit exports and imports. It’s easier for IDE navigation and refactoring.

- You have to include a description if you ever use `@ts-ignore`. Avoid this as much as possible.

- For nominal typing, see: https://michalzalecki.com/nominal-typing-in-typescript/#approach-4-intersection-types-and-brands

- Still can’t figure it out? Ask Yatharth, or check out https://react-typescript-cheatsheet.netlify.app/.


INTERNAL NOTES:

- DO use React.ReactElement, which represents any JSX element like <div/>, <Box/>, etc.
- DON'T use JSX.Element; you might as well use React.ReactElement in React projects.
- DON'T use React.FC and React.FunctionalComponent; they are deprecated.
- DON'T use React.ReactNode, which is a very broad type that includes anything React
   can render or accept as a return value from a component. This includes strings,
   numbers, null, undefined, and a bunch of other things. It’s too broad for our purposes.

We try to keep the types for these aliases as narrow as possible, instead of including
 everything that React.ReactNode does. This helps catch bugs and keep typing narrow.

While writing this, I consulted https://react-typescript-cheatsheet.netlify.app/.


Ambassador: Yatharth.

 */
import { AxleTheme } from '@/theme';
import { SxProps } from '@mui/system/styleFunctionSx/styleFunctionSx';
import React, { memo } from 'react';

export type DefiniteComponent = React.ReactElement;
export type Component = React.ReactElement | null;
export type ReturnComponent = React.ReactElement | null;
type CoerceableIntoComponent =
    | React.ReactElement
    | string
    | undefined
    | null
    | false
    | CoerceableIntoComponent[];
export type Children = CoerceableIntoComponent;

export type Style = React.CSSProperties;
export type SxStyle = SxProps<typeof AxleTheme>;

// If you wrap a generic component with memo(), you cannot explicitly provide type arguments anymore.
//  This wrapper over memo() fixes that. Copied from https://stackoverflow.com/a/70890101
export const genericMemo: <T>(component: T) => T & { displayName: string } = memo;
